import React, { useState, useMemo } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { CaretDown, List, X, SignOut } from "@phosphor-icons/react";
import NNPCLogo from "../../assets/NNPC LOGO PRIMARY WHITE 1.png";
import profile from "../../assets/profile.png";
import useAuth from "../../hooks/useAuth";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { UseUserinfo } from "../../adapters/Requests";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";

function TopNav() {
  const { auth, logout } = useAuth();
  const { data: userData } = UseUserinfo();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const apiClientPrivate = useAxiosPrivate();
  const loginSectionMode = import.meta.env.VITE_APPROVAL_LOGIN_SECTION_MODE;

  const perms = auth?.permissions || [];

  const canVerify = perms.includes("verify");
  const canApprove = perms.includes("approve");
  const canAccept = perms.includes("accept");

  const canDeploy = perms.includes("assign_supervisor");
  const canManage = perms.includes("manage");
  const canSupervise = perms.includes("supervise") || perms.includes("all");
  const canSecure = perms.includes("security_view");
  const canPayment =
    perms.includes("manage") ||
    perms.includes("all") ||
    perms.includes("assign_supervisor");
  const isAdmin = perms.includes("all") || perms.includes("admin");

  const operationalRoles = [
    "all",
    "admin",
    "verify",
    "approve",
    "accept",
    "assign_supervisor",
    "supervise",
    "manage",
  ];
  const hasOperationalRole = perms.some((p) => operationalRoles.includes(p));
  const showDashboardLink = hasOperationalRole;

  const managementLinks = useMemo(
    () =>
      [
        { show: canDeploy, path: "/deployments", label: "Deployments" },
        { show: canManage, path: "/endorsements", label: "Endorsements" },
        {
          show: canSupervise,
          path: "/supervision",
          label: "My Interns",
          additionalActivePaths: ["/monthlyassesment", "/finalassessment"],
        },
        { show: canSecure, path: "/security", label: "Security Checks" },
        {
          show: canPayment,
          path: "/report/payment-advice",
          label: "Payment Advice",
        },
      ].filter((l) => l.show),
    [canDeploy, canManage, canSupervise, canSecure, canPayment],
  );

  const appLinks = useMemo(
    () =>
      [
        { show: canVerify, path: "/allapp", label: "Verifications" },
        { show: canApprove, path: "/adapplications", label: "Approvals" },
      ].filter((l) => l.show),
    [canVerify, canApprove],
  );

  const hasAcceptance = canAccept;
  const hasAdmin = isAdmin;

  const totalIndividualLinks =
    (showDashboardLink ? 1 : 0) +
    appLinks.length +
    (hasAcceptance ? 1 : 0) +
    managementLinks.length +
    (hasAdmin ? 1 : 0);

  const groupMode = totalIndividualLinks > 5;

  const isLinkActive = (link) => {
    if (location.pathname === link.path) return true;
    if (link.additionalActivePaths) {
      return link.additionalActivePaths.some((path) =>
        location.pathname.includes(path),
      );
    }
    return false;
  };

  const getLinkClass = ({ isActive }) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ease-in-out whitespace-nowrap ${
      isActive
        ? "bg-green-50 text-green-700 shadow-sm"
        : "text-gray-600 hover:bg-green-50 hover:text-green-700"
    }`;

  const getTriggerClass = (isActive) =>
    `flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md outline-none transition-all duration-200 ease-in-out whitespace-nowrap cursor-pointer ${
      isActive
        ? "bg-green-50 text-green-700 shadow-sm"
        : "text-gray-600 hover:bg-green-50 hover:text-green-700"
    }`;

  const getItemClass = (link) => {
    const isActive =
      typeof link === "string"
        ? location.pathname === link
        : isLinkActive(link);

    return isActive
      ? "bg-green-50 text-green-700 font-medium cursor-pointer"
      : "text-gray-600 cursor-pointer hover:bg-green-50 hover:text-green-700";
  };

  const isAnyLinkActive = (links) => links.some((link) => isLinkActive(link));

  const isPathActive = (paths) => paths.some((p) => location.pathname === p);

  const handleLogout = async () => {
    try {
      const response = await apiClientPrivate.post("/users/logout");
      if (response.status === 200) {
        if (loginSectionMode !== "local") {
          await instance.logoutPopup();
        }
        localStorage.clear();
        await logout();
        navigate("/");
      }
    } catch (error) {
      console.error("Error logging out:", error);
      localStorage.clear();
      await logout();
      navigate("/");
    }
  };

  return (
    <nav className="fixed w-full bg-white border-b border-gray-100 z-50 h-16 shadow-sm">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link
              to={showDashboardLink ? "/dashboard" : "#"}
              className="flex-shrink-0 flex items-center gap-3 transition-opacity hover:opacity-90"
            >
              <img className="w-16 h-auto" src={NNPCLogo} alt="NNPC Logo" />
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {showDashboardLink && (
              <NavLink to="/dashboard" className={getLinkClass}>
                Dashboard
              </NavLink>
            )}

            {groupMode && appLinks.length > 1 ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={getTriggerClass(isAnyLinkActive(appLinks))}
                >
                  Applications <CaretDown size={14} className="mt-0.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48 shadow-lg border-gray-100">
                  {appLinks.map((link) => (
                    <DropdownMenuItem
                      key={link.path}
                      className={getItemClass(link)}
                      onClick={() => navigate(link.path)}
                    >
                      {link.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              appLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    getLinkClass({ isActive: isActive || isLinkActive(link) })
                  }
                >
                  {link.label}
                </NavLink>
              ))
            )}

            {hasAcceptance && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={getTriggerClass(
                    location.pathname.includes("/acc"),
                  )}
                >
                  Acceptance <CaretDown size={14} className="mt-0.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48 shadow-lg border-gray-100">
                  <DropdownMenuItem
                    className={getItemClass("/accapplications")}
                    onClick={() => navigate("/accapplications")}
                  >
                    Pending Acceptance
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={getItemClass("/accepted")}
                    onClick={() => navigate("/accepted")}
                  >
                    Accepted List
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {groupMode && managementLinks.length > 1 ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={getTriggerClass(isAnyLinkActive(managementLinks))}
                >
                  Management <CaretDown size={14} className="mt-0.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 shadow-lg border-gray-100">
                  {managementLinks.map((link) => (
                    <DropdownMenuItem
                      key={link.path}
                      className={getItemClass(link)}
                      onClick={() => navigate(link.path)}
                    >
                      {link.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              managementLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    getLinkClass({ isActive: isActive || isLinkActive(link) })
                  }
                >
                  {link.label}
                </NavLink>
              ))
            )}

            {hasAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={getTriggerClass(
                    isPathActive([
                      "/vacancysettings",
                      "/adminsettings",
                      "/managevacancy",
                      "/managecourses",
                      "/compliance",
                      "/editbuildings",
                    ]),
                  )}
                >
                  Admin <CaretDown size={14} className="mt-0.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48 shadow-lg border-gray-100">
                  <DropdownMenuItem
                    className={getItemClass("/vacancysettings")}
                    onClick={() => navigate("/vacancysettings")}
                  >
                    Vacancies
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={getItemClass("/managevacancy")}
                    onClick={() => navigate("/managevacancy")}
                  >
                    Create Vacancy
                  </DropdownMenuItem>
                  {/* <DropdownMenuSeparator /> */}
                  <DropdownMenuItem
                    className={getItemClass("/managecourses")}
                    onClick={() => navigate("/managecourses")}
                  >
                    Courses
                  </DropdownMenuItem>
                  {/* <DropdownMenuSeparator /> */}
                  <DropdownMenuItem
                    className={getItemClass("/compliance")}
                    onClick={() => navigate("/compliance")}
                  >
                    Learning Modules
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={getItemClass("/editbuildings")}
                    onClick={() => navigate("/editbuildings")}
                  >
                    Buildings
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={getItemClass("/adminsettings")}
                    onClick={() => navigate("/adminsettings")}
                  >
                    Users
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <NavLink to="/help" className={getLinkClass}>
              Help
            </NavLink>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-800">
                  {userData
                    ? `${userData.first_name} ${userData.last_name}`
                    : "User"}
                </p>
              </div>
              <div className="relative group cursor-pointer">
                <img
                  src={profile}
                  className="h-9 w-9 rounded-full border border-green-100 ring-2 ring-transparent group-hover:ring-green-50 transition-all duration-300"
                  alt="Profile"
                />
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all duration-200"
                title="Logout"
              >
                <SignOut size={20} />
              </button>
            </div>

            <div className="flex md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none transition-colors"
              >
                {isMobileMenuOpen ? <X size={24} /> : <List size={24} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-xl absolute w-full left-0 top-16 px-4 pt-2 pb-6 space-y-2 h-[calc(100vh-4rem)] overflow-y-auto animate-in slide-in-from-top-2">
          <div className="flex items-center gap-3 py-4 border-b border-gray-100 mb-2">
            <img
              src={profile}
              className="h-10 w-10 rounded-full border border-gray-200"
              alt="Profile"
            />
            <div>
              <p className="font-semibold text-gray-800">
                {userData
                  ? `${userData.first_name} ${userData.last_name}`
                  : "User"}
              </p>
            </div>
          </div>

          {showDashboardLink && (
            <NavLink
              to="/dashboard"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:bg-green-50 hover:text-green-700 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Dashboard
            </NavLink>
          )}

          {appLinks.length > 0 && (
            <div className="px-3 py-1 text-xs font-bold text-gray-400 uppercase tracking-wider mt-4 mb-1">
              Applications
            </div>
          )}
          {appLinks.map((link) => {
            const isActive = isLinkActive(link);
            return (
              <NavLink
                key={link.path}
                to={link.path}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                  isActive
                    ? "bg-green-50 text-green-700"
                    : "text-gray-600 hover:bg-green-50 hover:text-green-700"
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </NavLink>
            );
          })}

          {hasAcceptance && (
            <>
              <div className="px-3 py-1 text-xs font-bold text-gray-400 uppercase tracking-wider mt-4 mb-1">
                Acceptance
              </div>
              <NavLink
                to="/accapplications"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:bg-green-50 hover:text-green-700 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Pending Acceptance
              </NavLink>
              <NavLink
                to="/accepted"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:bg-green-50 hover:text-green-700 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Accepted List
              </NavLink>
            </>
          )}

          {managementLinks.length > 0 && (
            <div className="px-3 py-1 text-xs font-bold text-gray-400 uppercase tracking-wider mt-4 mb-1">
              Management
            </div>
          )}
          {managementLinks.map((link) => {
            const isActive = isLinkActive(link);
            return (
              <NavLink
                key={link.path}
                to={link.path}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                  isActive
                    ? "bg-green-50 text-green-700"
                    : "text-gray-600 hover:bg-green-50 hover:text-green-700"
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </NavLink>
            );
          })}

          {hasAdmin && (
            <>
              <div className="px-3 py-1 text-xs font-bold text-gray-400 uppercase tracking-wider mt-4 mb-1">
                Admin
              </div>
              <NavLink
                to="/vacancysettings"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:bg-green-50 hover:text-green-700 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Vacancies
              </NavLink>
              <NavLink
                to="/adminsettings"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:bg-green-50 hover:text-green-700 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Users
              </NavLink>
              <NavLink
                to="/managevacancy"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:bg-green-50 hover:text-green-700 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Create Vacancy
              </NavLink>
              <NavLink
                to="/managecourses"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:bg-green-50 hover:text-green-700 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Manage Courses
              </NavLink>
            </>
          )}

          <div className="border-t border-gray-100 my-4 pt-4">
            <NavLink
              to="/help"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:bg-green-50 hover:text-green-700 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Help
            </NavLink>
            <button
              onClick={handleLogout}
              className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 hover:bg-red-50 flex items-center gap-2 mt-2 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}

export default TopNav;
